import { useEffect, useMemo, useRef, useState } from 'react';
import type { TimUnreadMessageItem, TimUnreadMessages } from './tim-bridge';

const EMPTY_ITEMS: TimUnreadMessageItem[] = [];

export function groupUnreadMessages(items: TimUnreadMessageItem[]) {
  const peers = new Map<string, TimUnreadMessageItem>();
  for (const item of items) {
    if (
      item.unreadCount <= 0 ||
      item.message.isFromSelf ||
      !item.message.preview
    )
      continue;
    const previous = peers.get(item.peer.id);
    const latest =
      !previous || item.message.sentAt >= previous.message.sentAt
        ? item
        : previous;
    peers.set(item.peer.id, {
      ...latest,
      unreadCount: item.unreadCount + (previous?.unreadCount || 0)
    });
  }
  return Array.from(peers.values()).sort(
    (a, b) =>
      b.message.sentAt - a.message.sentAt || a.peer.id.localeCompare(b.peer.id)
  );
}

export function hasNewUnread(
  item: TimUnreadMessageItem,
  previous?: TimUnreadMessageItem
) {
  if (!previous) return true;
  const current = item.message;
  const old = previous.message;
  if (current.sentAt > old.sentAt) return true;
  if (current.sentAt < old.sentAt) return false;
  if (current.sequence !== undefined && old.sequence !== undefined)
    return current.sequence > old.sequence;
  if (current.id && old.id && current.id !== old.id) return true;
  return item.unreadCount > previous.unreadCount;
}

export function useTimUnreadToast(
  snapshot: TimUnreadMessages | null,
  imOpen: boolean,
  userId: string | null | undefined
) {
  const [visible, setVisible] = useState(
    document.visibilityState === 'visible'
  );
  const [, setDismissVersion] = useState(0);
  const dismissed = useRef(new Map<string, TimUnreadMessageItem>());
  const owner = useRef(userId);
  if (owner.current !== userId) {
    owner.current = userId;
    dismissed.current.clear();
  }
  const items = useMemo(
    () =>
      snapshot && snapshot.userId === userId ? snapshot.items : EMPTY_ITEMS,
    [snapshot, userId]
  );

  useEffect(() => {
    const update = () => setVisible(document.visibilityState === 'visible');
    const hide = () => setVisible(false);
    document.addEventListener('visibilitychange', update);
    window.addEventListener('pagehide', hide);
    window.addEventListener('pageshow', update);
    return () => {
      document.removeEventListener('visibilitychange', update);
      window.removeEventListener('pagehide', hide);
      window.removeEventListener('pageshow', update);
    };
  }, []);

  const dismiss = () => {
    items.forEach((item) => dismissed.current.set(item.conversationId, item));
    setDismissVersion((version) => version + 1);
  };

  useEffect(() => {
    if (!imOpen && visible) return;
    let changed = false;
    items.forEach((item) => {
      if (!hasNewUnread(item, dismissed.current.get(item.conversationId))) return;
      dismissed.current.set(item.conversationId, item);
      changed = true;
    });
    if (changed) setDismissVersion((version) => version + 1);
  }, [imOpen, items, visible]); // Hidden pages and opening IM end this presentation, without marking messages read.

  const peers = groupUnreadMessages(
    items.filter((item) =>
      hasNewUnread(item, dismissed.current.get(item.conversationId))
    )
  );

  return {
    peers,
    dismiss,
    shown: Boolean(userId && visible && !imOpen && peers.length)
  };
}
